import React, { useEffect, useState } from "react";
import { Check, X, Trash2, ExternalLink, Loader2, Gamepad2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function PendingDialog({ items, onReviewed }) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState("");
  const review = async (g, action) => {
    const reason = action === "reject" ? window.prompt("Reason for rejection (optional)") ?? null : "";
    if (reason === null) return;
    setBusy(g.id);
    try { await api.post(`/admin/gamehub/${g.id}/review`, { action, reason }); toast.success(action === "approve" ? `${g.name} published` : `${g.name} rejected`); onReviewed(); }
    catch { toast.error("Review failed"); } finally { setBusy(""); }
  };
  if (!items.length) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pending-games-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl" data-testid="pending-games-title">{items.length} game{items.length > 1 ? "s" : ""} waiting for approval</DialogTitle>
          <DialogDescription className="text-white/50">Approve to publish on the Games page, or reject with an optional reason.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {items.map((g) => (
            <div key={g.id} className="flex gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10" data-testid={`pending-game-${g.id}`}>
              <img src={mediaUrl(g.img)} alt="" className="w-24 h-16 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white truncate">{g.name}</div>
                <div className="text-xs text-white/50">{g.publisher} · by {g.uploader_name}</div>
                <p className="text-xs text-white/60 mt-1 line-clamp-2">{g.description}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => review(g, "approve")} disabled={busy === g.id} data-testid={`approve-game-${g.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs font-bold">{busy === g.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve</button>
                <button onClick={() => review(g, "reject")} disabled={busy === g.id} data-testid={`reject-game-${g.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-red-400 text-xs font-bold"><X size={12} /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminGameHub() {
  const [pub, setPub] = useState(null);
  const [pending, setPending] = useState([]);
  const load = () => {
    api.get("/admin/gamehub", { params: { status: "published" } }).then((r) => setPub(r.data.items)).catch(() => setPub([]));
    api.get("/admin/gamehub", { params: { status: "pending" } }).then((r) => setPending(r.data.items)).catch(() => setPending([]));
  };
  useEffect(() => { load(); }, []);
  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}" with all its ratings and comments?`)) return;
    try { await api.delete(`/admin/gamehub/${g.id}`); toast.success("Game deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="card-surface rounded-xl p-5 mt-6" data-testid="admin-published-games">
      <PendingDialog items={pending} onReviewed={load} />
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-white flex items-center gap-2"><Gamepad2 size={17} /> Published Games <span className="text-white/40 text-sm font-normal">({pub?.length ?? 0})</span></h3>
        {pending.length > 0 && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400" data-testid="pending-games-count">{pending.length} pending</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-white/40 border-b border-white/10"><th className="p-2.5">Game</th><th className="p-2.5">Publisher</th><th className="p-2.5">Uploaded by</th><th className="p-2.5">Rating</th><th className="p-2.5">Comments</th><th className="p-2.5 text-right">Actions</th></tr></thead>
          <tbody>
            {(pub || []).map((g) => (
              <tr key={g.id} className="border-b border-white/5" data-testid={`admin-game-row-${g.id}`}>
                <td className="p-2.5"><div className="flex items-center gap-2.5"><img src={mediaUrl(g.img)} alt="" className="w-12 h-8 rounded object-cover" /><span className="font-semibold text-white">{g.name}</span></div></td>
                <td className="p-2.5 text-white/60">{g.publisher || "—"}</td>
                <td className="p-2.5 text-white/60">{g.uploader_name}</td>
                <td className="p-2.5 text-white/80"><span className="inline-flex items-center gap-1"><Star size={12} className="text-yellow-400 fill-yellow-400" />{g.rating_count ? g.rating_avg.toFixed(1) : "–"} <span className="text-white/40 text-xs">({g.rating_count})</span></span></td>
                <td className="p-2.5 text-white/60">{g.comment_count}</td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <a href={`/games/${g.id}`} target="_blank" rel="noreferrer" className="inline-flex p-2 text-white/50 hover:text-white"><ExternalLink size={15} /></a>
                  <button onClick={() => remove(g)} data-testid={`admin-delete-game-${g.id}`} className="p-2 text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
